/**
 * Quick View Modal — Custom Element
 *
 * Provides a fully-featured product quick view modal that can be triggered
 * from any product card via a `.quick-view-btn[data-product-handle]` button.
 *
 * Dependencies (loaded before this file):
 *   - global.js       → trapFocus(), removeTrapFocus(), fetchConfig()
 *   - pubsub.js       → publish()
 *   - constants.js    → PUB_SUB_EVENTS
 *   - wishlist.js     → WishlistManager, WishlistButton
 *
 * @example
 *   <quick-view-modal aria-hidden="true"></quick-view-modal>
 *   <button class="quick-view-btn" data-product-handle="my-product">Quick View</button>
 */

class QuickViewModal extends HTMLElement {
  constructor() {
    super();

    /** @type {Map<string, Object>} Cache of fetched product JSON keyed by handle */
    this.productCache = new Map();

    /** @type {Object|null} Currently displayed product JSON */
    this.currentProduct = null;

    /** @type {Object|null} Currently selected variant object */
    this.currentVariant = null;

    /** @type {Object} Map of option name → selected value */
    this.selectedOptions = {};

    /** @type {number} Current carousel slide index (0-based) */
    this.currentImageIndex = 0;

    /** @type {number} Touch start X coordinate for swipe detection */
    this.touchStartX = 0;

    /** @type {number} Touch end X coordinate for swipe detection */
    this.touchEndX = 0;

    /** @type {number} Selected quantity */
    this.quantity = 1;

    /** @type {number|null} Autoplay interval ID */
    this.autoplayInterval = null;

    /** @type {number|null} Resume autoplay timeout ID */
    this.autoplayResumeTimeout = null;

    /** @type {number} Auto-slide interval in ms */
    this.autoplayDelay = 3500;
  }

  /* ------------------------------------------------------------------ */
  /*  Lifecycle                                                          */
  /* ------------------------------------------------------------------ */

  connectedCallback() {
    /* Close button */
    const closeBtn = this.querySelector('.quick-view__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    /* Overlay click */
    const overlay = this.querySelector('.quick-view__overlay');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    /* ESC key — scoped to document so it works regardless of focus */
    this._escHandler = (e) => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') {
        this.close();
      }
    };
    document.addEventListener('keydown', this._escHandler);
  }

  disconnectedCallback() {
    if (this._escHandler) {
      document.removeEventListener('keydown', this._escHandler);
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Open / Close                                                       */
  /* ------------------------------------------------------------------ */

  /**
   * Open the Quick View modal for a given product handle.
   * The modal stays completely hidden until all content and images
   * are fully loaded — no blank flash, no intermediate states.
   *
   * @param {string} handle — Product handle (URL slug)
   */
  async open(handle) {
    if (!handle) return;

    const loading = this.querySelector('.quick-view__loading');
    const content = this.querySelector('.quick-view__content');

    try {
      /* 1. Fetch product data (modal stays hidden) */
      let product;

      if (this.productCache.has(handle)) {
        product = this.productCache.get(handle);
      } else {
        const rootUrl = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${rootUrl}products/${handle}.js`);

        if (!response.ok) {
          console.error(`[QuickView] Failed to fetch product "${handle}": ${response.status}`);
          return;
        }

        product = await response.json();
        this.productCache.set(handle, product);
      }

      /* 2. Render content while modal is still invisible */
      if (loading) loading.style.display = 'none';
      if (content) { content.style.display = 'flex'; content.style.opacity = '1'; }
      this.renderModal(product);

      /* 3. Wait for the first image to load */
      const firstImg = content ? content.querySelector('.quick-view__image-slide img') : null;
      if (firstImg && !firstImg.complete) {
        await new Promise((resolve) => {
          firstImg.onload = resolve;
          firstImg.onerror = resolve;
          setTimeout(resolve, 2000);
        });
      }

      /* 4. NOW open the modal — content is fully ready */
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      /* Start auto-sliding carousel */
      this.startAutoplay();

      /* Trap focus inside the modal */
      const modalContent = content || this;
      requestAnimationFrame(() => {
        trapFocus(modalContent, this.querySelector('.quick-view__close'));
      });
    } catch (err) {
      console.error('[QuickView] Error opening modal:', err);
      this.close();
    }
  }

  /**
   * Show loading spinner, hide content.
   */
  showLoading() {
    const loading = this.querySelector('.quick-view__loading');
    const content = this.querySelector('.quick-view__content');
    if (loading) { loading.style.display = ''; loading.style.opacity = '1'; }
    if (content) { content.style.opacity = '0'; }
  }

  /**
   * Hide loading spinner, show content.
   */
  hideLoading() {
    const loading = this.querySelector('.quick-view__loading');
    const content = this.querySelector('.quick-view__content');
    if (loading) { loading.style.display = 'none'; loading.style.opacity = '0'; }
    if (content) { content.style.opacity = '1'; content.style.display = 'flex'; }
  }

  /**
   * Close the Quick View modal and restore page state.
   */
  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');

    /* Restore body scroll */
    document.body.style.overflow = '';

    /* Release focus trap */
    removeTrapFocus();

    /* Reset quantity */
    this.quantity = 1;

    /* Stop autoplay */
    this.stopAutoplay();

    /* Restore Shiprocket fastrrCollectionPage ID back to the main product page form */
    const backupFastrr = document.getElementById('_qv_fastrr_backup');
    if (backupFastrr) {
      backupFastrr.id = 'fastrrCollectionPage';
    }
    const qvFastrr = this.querySelector('.qv-fastrr-input');
    if (qvFastrr) {
      qvFastrr.id = 'fastrrCollectionPage-qv-closed';
    }
  }

  /**
   * Start automatic image sliding.
   */
  startAutoplay() {
    this.stopAutoplay();
    const images = this.currentProduct?.images || [];
    if (images.length <= 1) return;

    this.autoplayInterval = setInterval(() => {
      const nextIndex = (this.currentImageIndex + 1) % images.length;
      this.goToSlide(nextIndex);
    }, this.autoplayDelay);
  }

  /**
   * Stop automatic image sliding.
   */
  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    if (this.autoplayResumeTimeout) {
      clearTimeout(this.autoplayResumeTimeout);
      this.autoplayResumeTimeout = null;
    }
  }

  /**
   * Pause autoplay temporarily (on user interaction), resume after delay.
   */
  pauseAutoplay() {
    this.stopAutoplay();
    this.autoplayResumeTimeout = setTimeout(() => {
      this.startAutoplay();
    }, 6000);
  }

  /* ------------------------------------------------------------------ */
  /*  Render — Main                                                      */
  /* ------------------------------------------------------------------ */

  /**
   * Render the full modal content for the given product.
   *
   * @param {Object} product — Shopify product JSON
   */
  renderModal(product) {
    this.currentProduct = product;

    /* Pick the first available variant, falling back to the very first */
    const initialVariant =
      product.variants.find((v) => v.available) || product.variants[0];

    this.currentVariant = initialVariant;

    /* Build selectedOptions map from the initial variant */
    this.selectedOptions = {};
    product.options.forEach((opt, idx) => {
      const optName = this._getOptionName(opt);
      this.selectedOptions[optName] = initialVariant[`option${idx + 1}`];
    });

    /* Reset quantity & image index */
    this.quantity = 1;
    this.currentImageIndex = 0;

    /* Render each section */
    this.renderImages(product, initialVariant);
    this.renderInfo(product, initialVariant);
    this.renderVariants(product);
    this.renderQuantity();
    this.renderActions(product, initialVariant);
  }

  /* ------------------------------------------------------------------ */
  /*  Render — Images / Carousel                                         */
  /* ------------------------------------------------------------------ */

  /**
   * Build the image carousel with thumbnails, dots, and navigation arrows.
   *
   * @param {Object} product — Product JSON
   * @param {Object} variant — Currently selected variant
   */
  renderImages(product, variant) {
    const container = this.querySelector('.quick-view__main-image');
    if (!container) return;

    const images = product.images && product.images.length > 0
      ? product.images
      : []; /* Handle products with zero images */

    /* ---- Slides ---- */
    const slidesHtml = images.length > 0
      ? images.map((src, i) => `
          <div class="quick-view__image-slide" data-index="${i}">
            <img src="${this._sizedImageUrl(src, '800x')}"
                 alt="${product.title} - Image ${i + 1}"
                 loading="${i === 0 ? 'eager' : 'lazy'}"
                 width="800" height="800"
                 draggable="false">
          </div>`).join('')
      : `<div class="quick-view__image-slide quick-view__image-slide--placeholder">
           <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 525.5 525.5" fill="currentColor" aria-hidden="true">
             <path d="M375.5 345.2c0-.1 0-.1 0 0 0-.1 0-.1 0 0-1.1-2.9-2.3-5.5-3.4-7.8-1.7-3.6-3.5-6.4-5.1-8.5-1.6-2.2-2.9-3.5-3.9-4.4-1-.8-1.5-1.1-1.6-1.1-.4-1.4-.9-2.7-1.5-3.8-.6-1.1-1.1-2-1.5-2.5-.4-.6-.7-.9-.8-.9s.1.4.4 1.2c.3.8.6 1.9.8 3.3-1.8-2.5-3.9-4.3-6.1-5.6-2.2-1.3-4.5-1.9-6.9-1.9-2.4 0-5 .5-7.5 1.6 2.5.1 5 .8 7.2 2.1 2.3 1.3 4.3 3.1 6.1 5.5-1.8-1.3-3.5-2.1-5.1-2.5-1.6-.3-3-.3-4.3.2-1.3.5-2.4 1.3-3.4 2.4-1 1.1-1.8 2.6-2.5 4.4 1.3-.2 2.6-.1 3.9.3 1.3.4 2.5 1.3 3.7 2.5 1.1 1.3 2.2 3 3.2 5.2.9 2.2 1.8 4.8 2.4 8.1 1.1-2.3 1.6-4.3 1.7-6.1.1-1.8-.2-3.4-.9-4.7s-1.7-2.3-2.9-3c-1.2-.8-2.5-1.2-3.9-1.4 1.3-.6 2.7-.8 4.1-.8 1.4 0 2.8.4 4.1 1.1 1.3.7 2.4 1.7 3.4 3 1 1.3 1.7 2.9 2.1 4.8.4-1.4.5-2.7.3-3.9-.2-1.2-.5-2.2-1-3.1-.5-.9-1-1.6-1.5-2.2-.5-.6-.9-1-.9-1 .5.3 1.1.7 1.9 1.5.8.7 1.6 1.8 2.6 3.3.9 1.5 1.8 3.5 2.7 6 .9 2.5 1.6 5.8 2.1 9.8.3-1.3.4-2.7.3-4.1-.1-1.4-.3-2.7-.7-4-1-3.2-2.7-6.2-5-8.8 2.3 1.4 4.1 3.2 5.4 5.4 1.3 2.2 2.1 4.5 2.5 7 .3 2.5.2 4.9-.4 7.3-.5 2.4-1.4 4.5-2.6 6.4.7-1.8 1.1-3.5 1.3-5.1.2-1.6.2-3-.1-4.3-.2-1.3-.7-2.4-1.3-3.3-.6-.9-1.2-1.6-1.9-2.1-.6-.5-1.2-.9-1.6-1.1-.4-.2-.6-.3-.6-.3s.2.3.5.7c.3.5.7 1.2 1 2.1.3 1 .5 2.2.4 3.7-.1 1.5-.5 3.3-1.3 5.3-.5 1.3-1.2 2.6-2.1 3.8-.8 1.2-1.8 2.4-2.9 3.3-1.1 1-2.3 1.8-3.5 2.4-1.3.6-2.6 1-3.8 1.2 2.4-.2 4.5-.9 6.3-1.8 1.8-1 3.2-2.1 4.4-3.3 1.2-1.2 2.2-2.4 2.9-3.4.8-1.1 1.3-1.9 1.6-2.3-1.3 4.5-3.3 8.1-5.8 11-2.5 2.9-5.2 5-7.7 6.4 4.1-.4 7.5-2.3 10.1-5.3 2.6-3 4.3-6.7 5-10.8.2-1.4.3-2.8.2-4.2-.1-1.4-.3-2.7-.6-3.8-.3-1.1-.7-2.1-1.2-2.8-.5-.8-1-1.3-1.3-1.5s-.5-.3-.5-.3c1.3.7 2.3 2 3.1 3.7.7 1.7 1.1 3.5 1.2 5.5.1 2-.1 4-.6 5.8-.5 1.9-1.2 3.5-2 4.8 2-2.5 3.1-5.3 3.5-8.2.4-2.9.1-5.6-.6-8-.7-2.4-1.7-4.3-2.7-5.8-1-1.4-1.8-2.4-2.3-2.8-.5-.5-.7-.6-.7-.6.5.6 1.2 1.6 2 3 .8 1.4 1.5 3.1 2.1 5.2.6 2.1.8 4.5.5 7.1-.2 2.6-.9 5.3-2.3 8.2-.4.8-.8 1.5-1.3 2.3-.5.7-1 1.4-1.5 2.1-.5.7-1 1.3-1.6 1.9l-1.7 1.5c-2.3 2-4.8 3.5-7.4 4.3-2.6.8-5.2 1.1-7.7.9-2.5-.2-5-.8-7.2-2-2.2-1.1-4-2.7-5.5-4.5.1 1.2.6 2.4 1.3 3.6.7 1.1 1.7 2.2 2.9 3.1 1.2.9 2.6 1.6 4.2 2.1 1.6.5 3.3.7 5.1.7-2.5 1.1-5 1.6-7.5 1.4-2.5-.1-4.8-.8-6.8-1.8-2-.9-3.7-2.2-5.1-3.6-1.4-1.4-2.4-2.8-3.1-4.1l-1.2-2.1c-.3-.7-.4-1.1-.4-1.1s0 .2.1.5c0 .3.1.7.3 1.3.4 1.7 1.4 4.1 3.2 6.3 1.8 2.2 4.5 4.2 8 5.2-2.3.1-4.5-.2-6.4-.9-2-.7-3.6-1.7-5-3 6.2 7.8 14.4 10.7 19.4 11.2 2.5.2 4.4.1 5.7-.2 1.2-.3 1.8-.6 1.8-.6s-.6.4-1.9.9c-1.3.5-3.3.9-5.9 1-2.6.1-5.8-.3-9.2-1.7-3.4-1.4-7.2-3.8-10.6-7.7 1.9 3.8 4.6 6.7 7.5 8.7 2.9 2 5.9 3.1 8.6 3.6 2.7.5 5.1.4 6.9 0 1.8-.4 2.9-.8 2.9-.8s-.3.7-1.7 1.6c-1.4.9-3.8 1.8-7 2-3.2.2-7.2-.7-11-3.3-3.8-2.6-7.4-7.1-9.6-13.6 1.1 3.7 2.8 6.7 4.8 9 2 2.3 4.3 3.9 6.5 4.9 2.3 1 4.5 1.4 6.3 1.4 1.9 0 3.3-.4 3.3-.4s-.5.7-2 1.5c-1.5.8-4 1.3-7.1.9-3.1-.4-6.8-2-10.1-5.2-3.3-3.2-6-8.2-7.2-14.8 1.5 5.6 4 9.8 6.7 12.7 2.7 2.9 5.5 4.5 7.7 5.4 2.3.8 3.9.8 3.9.8s-.6.4-2 .6c-1.4.2-3.5.1-6-.7-2.5-.9-5.3-2.8-7.7-5.9-2.4-3.1-4.2-7.6-4.8-13.1.1 2.4.5 4.7 1.2 6.7.7 2 1.6 3.8 2.8 5.4 1.1 1.6 2.4 2.8 3.7 3.8 1.3 1 2.6 1.7 3.5 2.1.9.4 1.5.5 1.5.5s-1.8-.1-4.2-1.5c-1.2-.7-2.6-1.7-3.8-3.2-1.3-1.4-2.4-3.3-3.2-5.5-.8-2.2-1.2-4.7-1.2-7.6v-2.7l-.2-.6c-1.1-2.8-1.7-5.4-2-7.8-.3-2.4-.3-4.6-.2-6.5.2-1.9.4-3.5.7-4.8.3-1.3.7-2.2.9-2.8.2-.6.4-.9.4-.9s-.2 1.1-.4 3c-.2 1.9-.3 4.7.1 8.1.4 3.4 1.3 7.3 3.2 11.3-.4-3.1-.4-5.8-.1-8.1.3-2.3.8-4.3 1.4-5.8.6-1.6 1.3-2.8 1.8-3.5.5-.7.9-1.1.9-1.1s-.3 2.1-.3 5.1c0 3 .4 6.8 1.8 10.9-.4-2.9-.5-5.5-.3-7.7.2-2.2.6-4 1.2-5.5.6-1.5 1.2-2.6 1.7-3.4.5-.8.9-1.2.9-1.2s-.3 2-.3 4.9c0 2.9.4 6.6 1.7 10.5-.4-2.7-.5-5.1-.3-7.2.2-2.1.5-3.8 1-5.2.5-1.4 1.1-2.4 1.5-3.1.4-.7.7-1 .7-1z"/>
           </svg>
         </div>`;

    /* ---- Arrows ---- */
    const arrowsHtml = images.length > 1
      ? `<button class="quick-view__arrow quick-view__arrow--prev" aria-label="Previous image" type="button">
           <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><polyline points="12,4 6,10 12,16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
         </button>
         <button class="quick-view__arrow quick-view__arrow--next" aria-label="Next image" type="button">
           <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><polyline points="8,4 14,10 8,16" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
         </button>`
      : '';

    /* ---- Thumbnails (inner HTML only, container exists in markup) ---- */
    const thumbsHtml = images.length > 1
      ? images.map((src, i) => `
              <button class="quick-view__thumbnail${i === 0 ? ' is-active' : ''}"
                      data-index="${i}" type="button"
                      aria-label="View image ${i + 1}">
                <img src="${this._sizedImageUrl(src, '100x100')}"
                     alt="${product.title} thumbnail ${i + 1}"
                     loading="lazy" width="60" height="60" draggable="false">
              </button>`).join('')
      : '';

    /* ---- Dots (inner HTML only, container exists in markup) ---- */
    const dotsHtml = images.length > 1
      ? images.map((_, i) => `
              <button class="quick-view__dot${i === 0 ? ' is-active' : ''}"
                      data-index="${i}" type="button"
                      aria-label="Go to image ${i + 1}"></button>`).join('')
      : '';

    /* Place carousel (slides + arrows) in main-image container */
    container.innerHTML = `
      <div class="quick-view__image-track">${slidesHtml}</div>
      ${arrowsHtml}`;

    /* Place thumbnails and dots in their sibling containers */
    const imagesParent = this.querySelector('.quick-view__images');
    if (imagesParent) {
      const thumbsContainer = imagesParent.querySelector(':scope > .quick-view__thumbnails');
      const dotsContainer = imagesParent.querySelector(':scope > .quick-view__dots');
      if (thumbsContainer) thumbsContainer.innerHTML = images.length > 1 ? thumbsHtml : '';
      if (dotsContainer) dotsContainer.innerHTML = images.length > 1 ? dotsHtml : '';
    }

    /* ---- Event listeners ---- */
    this._attachImageListeners(container, images);

    /* Also attach listeners on thumbnails and dots which are siblings */
    if (imagesParent) {
      this._attachThumbAndDotListeners(imagesParent, images);
    }

    /* If the variant has a featured image, jump to that slide */
    if (variant && variant.featured_image) {
      const variantImgIndex = this._findImageIndex(images, variant.featured_image.src);
      if (variantImgIndex > 0) {
        this.goToSlide(variantImgIndex);
      }
    }
  }

  /**
   * Attach navigation, thumbnail, dot, and swipe listeners for the carousel.
   *
   * @param {HTMLElement} container — The `.quick-view__main-image` element
   * @param {string[]}    images   — Array of product image URLs
   */
  _attachImageListeners(container, images) {
    if (images.length <= 1) return;

    /* Arrows — pause autoplay on manual nav */
    const prevBtn = container.querySelector('.quick-view__arrow--prev');
    const nextBtn = container.querySelector('.quick-view__arrow--next');
    if (prevBtn) prevBtn.addEventListener('click', () => { this.pauseAutoplay(); this.goToSlide(this.currentImageIndex - 1); });
    if (nextBtn) nextBtn.addEventListener('click', () => { this.pauseAutoplay(); this.goToSlide(this.currentImageIndex + 1); });

    /* Thumbnails */
    container.querySelectorAll('.quick-view__thumbnail').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.pauseAutoplay();
        this.goToSlide(parseInt(thumb.dataset.index, 10));
      });
    });

    /* Dots */
    container.querySelectorAll('.quick-view__dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        this.pauseAutoplay();
        this.goToSlide(parseInt(dot.dataset.index, 10));
      });
    });

    /* Touch / Swipe */
    const track = container.querySelector('.quick-view__image-track');
    if (track) {
      track.addEventListener('touchstart', (e) => {
        this.touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      track.addEventListener('touchmove', (e) => {
        this.touchEndX = e.changedTouches[0].screenX;
      }, { passive: true });

      track.addEventListener('touchend', () => {
        const diff = this.touchStartX - this.touchEndX;
        const threshold = 50;
        if (Math.abs(diff) > threshold) {
          this.pauseAutoplay();
          if (diff > 0) {
            this.goToSlide(this.currentImageIndex + 1);
          } else {
            this.goToSlide(this.currentImageIndex - 1);
          }
        }
      });
    }
  }

  /**
   * Attach click listeners for thumbnails and dots that live in sibling containers.
   *
   * @param {HTMLElement} imagesParent — The `.quick-view__images` element
   * @param {string[]}    images      — Array of product image URLs
   */
  _attachThumbAndDotListeners(imagesParent, images) {
    if (images.length <= 1) return;

    /* Thumbnails */
    imagesParent.querySelectorAll('.quick-view__thumbnails .quick-view__thumbnail').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        this.pauseAutoplay();
        this.goToSlide(parseInt(thumb.dataset.index, 10));
      });
    });

    /* Dots */
    imagesParent.querySelectorAll('.quick-view__dots .quick-view__dot').forEach((dot) => {
      dot.addEventListener('click', () => {
        this.pauseAutoplay();
        this.goToSlide(parseInt(dot.dataset.index, 10));
      });
    });
  }

  /**
   * Navigate the carousel to a specific slide index.
   *
   * @param {number} index — Target slide index (will be clamped)
   */
  goToSlide(index) {
    const images = this.currentProduct?.images || [];
    if (images.length === 0) return;

    /* Clamp */
    const clamped = Math.max(0, Math.min(index, images.length - 1));
    this.currentImageIndex = clamped;

    /* Move track */
    const track = this.querySelector('.quick-view__image-track');
    if (track) {
      track.style.transform = `translateX(-${clamped * 100}%)`;
    }

    /* Update active thumbnail */
    this.querySelectorAll('.quick-view__thumbnail').forEach((t) => {
      t.classList.toggle('is-active', parseInt(t.dataset.index, 10) === clamped);
    });

    /* Update active dot */
    this.querySelectorAll('.quick-view__dot').forEach((d) => {
      d.classList.toggle('is-active', parseInt(d.dataset.index, 10) === clamped);
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Render — Product Info                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Populate the product information panel (title, price, description, etc.)
   *
   * @param {Object} product — Product JSON
   * @param {Object} variant — Selected variant
   */
  renderInfo(product, variant) {
    /* Vendor */
    const vendorEl = this.querySelector('.quick-view__vendor');
    if (vendorEl) {
      const showVendor = this.dataset.showVendor !== 'false';
      vendorEl.textContent = product.vendor || '';
      vendorEl.style.display = (showVendor && product.vendor) ? '' : 'none';
    }

    /* Title */
    const titleEl = this.querySelector('.quick-view__title');
    if (titleEl) titleEl.textContent = product.title;

    /* Price */
    this._updatePriceDisplay(variant);

    /* Availability */
    this._updateAvailability(variant);

    /* SKU */
    const skuEl = this.querySelector('.quick-view__sku');
    if (skuEl) {
      const showSku = this.dataset.showSku === 'true';
      skuEl.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
      skuEl.style.display = (showSku && variant.sku) ? '' : 'none';
    }

    /* Description — truncated text only */
    const showDescription = this.dataset.showDescription !== 'false';
    const descEl = this.querySelector('.quick-view__description');
    if (descEl && product.description && showDescription) {
      const stripped = this._stripHtml(product.description);
      const maxLen = 250;
      const truncated = stripped.length > maxLen
        ? stripped.substring(0, maxLen).replace(/\s+\S*$/, '') + '…'
        : stripped;

      descEl.innerHTML = `<div class="quick-view__desc-summary">${truncated}</div>`;
    } else if (descEl) {
      descEl.innerHTML = '';
      descEl.style.display = 'none';
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Render — Variants                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Render variant option pill selectors for each product option.
   * Handles single-variant products gracefully (hides options section).
   *
   * @param {Object} product — Product JSON
   */
  renderVariants(product) {
    const container = this.querySelector('.quick-view__variants');
    if (!container) return;

    /* Single-variant products: hide variant selectors */
    const firstOptName = this._getOptionName(product.options[0]);
    if (product.variants.length <= 1 && product.options.length === 1 && firstOptName === 'Title') {
      container.innerHTML = '';
      container.style.display = 'none';
      return;
    }

    container.style.display = '';
    let html = '';

    product.options.forEach((opt, optionIndex) => {
      const optionName = this._getOptionName(opt);
      const isColorOption = this._isColorOption(optionName);
      /* Collect unique values for this option */
      const values = [];
      product.variants.forEach((v) => {
        const val = v[`option${optionIndex + 1}`];
        if (val && !values.includes(val)) values.push(val);
      });

      html += `
        <div class="quick-view__option-group${isColorOption ? ' quick-view__option-group--color' : ''}" data-option-index="${optionIndex}">
          <label class="quick-view__option-label">
            ${optionName}: <span class="quick-view__option-selected-value">${this.selectedOptions[optionName] || ''}</span>
          </label>
          <div class="quick-view__option-values${isColorOption ? ' quick-view__option-values--color' : ''}">
            ${values.map((val) => {
              const isSelected = this.selectedOptions[optionName] === val;
              const isUnavailable = this._isOptionUnavailable(optionIndex, val, product);

              if (isColorOption) {
                const hex = this._colorNameToHex(val);
                return `<button type="button"
                          class="quick-view__color-swatch${isSelected ? ' is-selected' : ''}${isUnavailable ? ' is-unavailable' : ''}"
                          data-option-name="${optionName}"
                          data-option-index="${optionIndex}"
                          data-value="${val}"
                          title="${val}${isUnavailable ? ' (Unavailable)' : ''}"
                          style="background-color: ${hex};">
                          <span class="visually-hidden">${val}</span>
                        </button>`;
              }

              return `<button type="button"
                        class="quick-view__option-value${isSelected ? ' is-selected' : ''}${isUnavailable ? ' is-unavailable' : ''}"
                        data-option-name="${optionName}"
                        data-option-index="${optionIndex}"
                        data-value="${val}"
                        ${isUnavailable ? 'title="Unavailable"' : ''}>
                        ${val}
                      </button>`;
            }).join('')}
          </div>
        </div>`;
    });

    container.innerHTML = html;

    /* Attach click handlers to pills and color swatches */
    container.querySelectorAll('.quick-view__option-value, .quick-view__color-swatch').forEach((pill) => {
      pill.addEventListener('click', () => {
        const optName = pill.dataset.optionName;
        const optValue = pill.dataset.value;

        /* Update selected option */
        this.selectedOptions[optName] = optValue;

        /* Find matching variant */
        const matchedVariant = this.findVariantFromOptions();
        if (matchedVariant) {
          this.updateVariant(matchedVariant);
        }

        /* Update the displayed value next to the label */
        const group = pill.closest('.quick-view__option-group');
        const valSpan = group?.querySelector('.quick-view__option-selected-value');
        if (valSpan) valSpan.textContent = optValue;

        /* Re-render all pills to update selection & availability states */
        this._updatePillStates();
      });
    });
  }

  /**
   * Check if a particular option value leads to no available variant
   * (considering the currently selected other options).
   *
   * @param {number} optionIndex — 0-based option position
   * @param {string} value       — Option value to test
   * @param {Object} product     — Product JSON
   * @returns {boolean}
   */
  _isOptionUnavailable(optionIndex, value, product) {
    /* Build a hypothetical selectedOptions with this value swapped in */
    const testOptions = { ...this.selectedOptions };
    const optionName = this._getOptionName(product.options[optionIndex]);
    testOptions[optionName] = value;

    /* Check if ANY variant matches this combination and is available */
    return !product.variants.some((v) => {
      let matches = true;
      product.options.forEach((opt, idx) => {
        const name = this._getOptionName(opt);
        if (testOptions[name] && v[`option${idx + 1}`] !== testOptions[name]) {
          matches = false;
        }
      });
      return matches && v.available;
    });
  }

  /**
   * Update visual state of all variant pill buttons.
   */
  _updatePillStates() {
    const product = this.currentProduct;
    if (!product) return;

    this.querySelectorAll('.quick-view__option-value[data-option-name], .quick-view__color-swatch[data-option-name]').forEach((pill) => {
      const optName = pill.dataset.optionName;
      const optIndex = parseInt(pill.dataset.optionIndex, 10);
      const value = pill.dataset.value;

      pill.classList.toggle('is-selected', this.selectedOptions[optName] === value);
      pill.classList.toggle('is-unavailable', this._isOptionUnavailable(optIndex, value, product));
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Variant Update                                                     */
  /* ------------------------------------------------------------------ */

  /**
   * Update the modal UI to reflect a newly selected variant.
   *
   * @param {Object} variant — The new variant object
   */
  updateVariant(variant) {
    this.currentVariant = variant;

    /* Price, compare_at, discount, availability, SKU */
    this._updatePriceDisplay(variant);
    this._updateAvailability(variant);

    /* SKU */
    const skuEl = this.querySelector('.quick-view__sku');
    if (skuEl) {
      skuEl.textContent = variant.sku ? `SKU: ${variant.sku}` : '';
      skuEl.style.display = variant.sku ? '' : 'none';
    }

    /* Add to Cart button */
    const atcBtn = this.querySelector('.quick-view__add-to-cart');
    if (atcBtn) {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? 'Add to Cart' : 'Sold Out';
    }

    /* Update the Buy Now form's variant ID */
    const srContainer = this.querySelector('.quick-view__sr-buy-container');
    if (srContainer) {
      /* Update variant ID in the buy-now form */
      const variantInput = srContainer.querySelector('input[name="id"], .product-variant-id');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      /* Update quantity in the buy-now form */
      const qtyInput = srContainer.querySelector('input[name="quantity"]');
      if (qtyInput) {
        qtyInput.value = this.quantity || 1;
      }

      /* Show/hide the Buy Now button based on availability */
      const paymentBtn = srContainer.querySelector('.shopify-payment-button');
      if (paymentBtn) {
        paymentBtn.style.display = variant.available ? '' : 'none';
      }
    }

    /* Jump carousel to variant image */
    if (variant.featured_image && this.currentProduct) {
      const idx = this._findImageIndex(this.currentProduct.images, variant.featured_image.src);
      if (idx >= 0) this.goToSlide(idx);
    }

    /* Update pill selected states */
    this._updatePillStates();
  }

  /**
   * Find the variant that matches the currently selected options.
   *
   * @returns {Object|null} — Matching variant or null
   */
  findVariantFromOptions() {
    if (!this.currentProduct) return null;

    return this.currentProduct.variants.find((v) => {
      return this.currentProduct.options.every((opt, idx) => {
        const optName = this._getOptionName(opt);
        return v[`option${idx + 1}`] === this.selectedOptions[optName];
      });
    }) || null;
  }

  /* ------------------------------------------------------------------ */
  /*  Render — Quantity                                                  */
  /* ------------------------------------------------------------------ */

  /**
   * Render the quantity selector with minus/plus buttons and input.
   */
  renderQuantity() {
    const container = this.querySelector('.quick-view__quantity');
    if (!container) return;

    /* Check if quantity selector should be shown */
    const showQuantity = this.dataset.showQuantity !== 'false';
    if (!showQuantity) {
      container.style.display = 'none';
      return;
    }
    container.style.display = '';
    this.quantity = 1;

    container.innerHTML = `
      <label class="quick-view__quantity-label">Quantity</label>
      <div class="quick-view__quantity-wrapper">
        <button class="quick-view__quantity-btn quick-view__qty-minus" type="button" aria-label="Decrease quantity">
          <span aria-hidden="true">−</span>
        </button>
        <input class="quick-view__quantity-input" type="number" value="1" min="1" max="99" aria-label="Quantity">
        <button class="quick-view__quantity-btn quick-view__qty-plus" type="button" aria-label="Increase quantity">
          <span aria-hidden="true">+</span>
        </button>
      </div>`;

    const input = container.querySelector('.quick-view__quantity-input');
    const minusBtn = container.querySelector('.quick-view__qty-minus');
    const plusBtn = container.querySelector('.quick-view__qty-plus');

    minusBtn.addEventListener('click', () => {
      if (this.quantity > 1) {
        this.quantity--;
        input.value = this.quantity;
      }
    });

    plusBtn.addEventListener('click', () => {
      if (this.quantity < 99) {
        this.quantity++;
        input.value = this.quantity;
      }
    });

    input.addEventListener('change', () => {
      let val = parseInt(input.value, 10);
      if (isNaN(val) || val < 1) val = 1;
      if (val > 99) val = 99;
      this.quantity = val;
      input.value = val;
    });
  }

  renderActions(product, variant) {
    const container = this.querySelector('.quick-view__actions');
    if (!container) return;

    const isSoldOut = !variant.available;
    const showBuyNow = this.dataset.showBuyNow !== 'false';

    /* --- Add to Cart button --- */
    const atcBtn = container.querySelector('.quick-view__add-to-cart');
    if (atcBtn) {
      atcBtn.textContent = isSoldOut ? 'Sold Out' : 'Add to Cart';
      atcBtn.disabled = isSoldOut;
      atcBtn.style.display = '';
      /* Re-attach handler (clone to remove old listeners) */
      const newAtcBtn = atcBtn.cloneNode(true);
      atcBtn.parentNode.replaceChild(newAtcBtn, atcBtn);
      if (!isSoldOut) {
        newAtcBtn.addEventListener('click', () => this._handleAddToCart());
      }
    }

    /* --- Buy Now / Shiprocket button --- */
    const srContainer = container.querySelector('.quick-view__sr-buy-container');
    if (srContainer) {
      if (showBuyNow && !isSoldOut) {
        this._injectBuyNowButton(srContainer, product, variant);
      } else {
        srContainer.innerHTML = '';
      }
    }

    /* ---- Wishlist (separate container below description) ---- */
    const showWishlist = this.dataset.showWishlist !== 'false';
    const wishlistSection = this.querySelector('.quick-view__wishlist-section');
    if (wishlistSection && showWishlist) {
      wishlistSection.innerHTML = `
        <wishlist-button data-product-handle="${product.handle}">
          <button type="button"
                  class="card__wishlist-btn${WishlistManager.isWishlisted(product.handle) ? ' is-wishlisted' : ''}"
                  aria-label="${WishlistManager.isWishlisted(product.handle) ? 'Remove from wishlist' : 'Add to wishlist'}">
            <svg class="icon-heart" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </button>
        </wishlist-button>`;
    } else if (wishlistSection) {
      wishlistSection.innerHTML = '';
    }

    /* ---- View Full Details link ---- */
    const showViewFull = this.dataset.showViewFull !== 'false';
    const viewFullLink = this.querySelector('.quick-view__view-full');
    if (viewFullLink) {
      viewFullLink.href = `/products/${product.handle}`;
      viewFullLink.style.display = showViewFull ? '' : 'none';
    }
  }

  /**
   * Inject the Buy Now button into the quick view.
   *
   * Strategy for Shiprocket integration:
   *  - Create a real Shopify product form with all the elements Shiprocket expects
   *  - Give the fastrrCollectionPage input a unique ID initially
   *  - On Buy Now click: temporarily swap the ID to 'fastrrCollectionPage' so
   *    Shiprocket's global submit interceptor picks up OUR form, then submit
   *  - Shiprocket's external script (fastrr-boost-ui.pickrr.com/shopify.js)
   *    listens for form submit events and checks for getElementById('fastrrCollectionPage')
   *
   * @param {HTMLElement} container — The `.quick-view__sr-buy-container` element
   * @param {Object} product — Product JSON
   * @param {Object} variant — Selected variant
   */
  _injectBuyNowButton(container, product, variant) {
    const productUrl = `/products/${product.handle}`;
    const uniqueFormId = `qv-buy-form-${Date.now()}`;
    const uniqueFastrrId = `fastrrCollectionPage-qv-${Date.now()}`;

    container.innerHTML = `
      <form method="post" action="/cart/add" accept-charset="UTF-8"
            id="${uniqueFormId}"
            class="shopify-product-form qv-buy-now-form"
            data-type="add-to-cart-form"
            data-product-id="${product.id}"
            data-product-handle="${product.handle}"
            enctype="multipart/form-data">
        <input type="hidden" name="form_type" value="product">
        <input type="hidden" name="utf8" value="\u2713">
        <input type="hidden" name="id" value="${variant.id}" class="qv-buy-variant-id product-variant-id">
        <input type="hidden" name="quantity" value="${this.quantity || 1}">
        <input type="hidden" value="true" id="${uniqueFastrrId}" class="qv-fastrr-input fastrrCollectionPage">
        <input type="hidden" value="${productUrl}" name="productUrl">
        <div data-shopify="payment-button" class="shopify-payment-button qv-payment-button-wrapper">
          <button type="button"
                  class="shopify-payment-button__button shopify-payment-button__button--unbranded qv-buy-now-btn"
                  style="width:100%; padding: 1.2rem 2rem; font-size: 1.4rem; font-weight: 600;
                         letter-spacing: 0.1rem; text-transform: uppercase; cursor: pointer;
                         background-color: rgb(var(--color-button)); color: rgb(var(--color-button-text));
                         border: none; border-radius: 0;">
            BUY IT NOW
          </button>
        </div>
      </form>`;

    const form = container.querySelector('form');
    const fastrrInput = container.querySelector('.qv-fastrr-input');

    /*
     * EVENT DELEGATION FIX:
     * Attach the click handler to the container (`.quick-view__sr-buy-container`)
     * instead of directly to the button. This is critical because:
     *
     *   1. We attach a handler to the `qv-buy-now-btn` button.
     *   2. `_triggerShiprocketInit` calls `reRenderBuyNowButton()` after 300–800ms.
     *   3. Shiprocket replaces the `.shopify-payment-button` div's contents,
     *      destroying our original button AND its event listener.
     *   4. The new Shiprocket button has no working handler in the quick view
     *      context (especially on the product page where Shiprocket is already
     *      bound to the main product form).
     *
     * By delegating to the container, clicks on ANY button inside it
     * (original or Shiprocket-replaced) bubble up and are handled correctly.
     *
     * We always use `_fallbackBuyNow()` (add to cart → redirect to /checkout)
     * because Shiprocket intercepts the /checkout URL when checkout page
     * takeover is enabled, making this approach work universally.
     */

    /* Remove any previous delegation handler to avoid duplicates
       (this method is called again on variant changes) */
    if (container._qvBuyHandler) {
      container.removeEventListener('click', container._qvBuyHandler, true);
    }

    container._qvBuyHandler = (e) => {
      /*
       * Don't filter by element type. Shiprocket replaces our <button> with
       * <div> elements (e.g. .shiprocket-headless) on some products.
       * Since this container ONLY holds the buy-now form and its button,
       * any click inside it is a buy-now intent.
       *
       * Ignore clicks on hidden form inputs (they shouldn't be clickable,
       * but guard against edge cases).
       */
      if (e.target.tagName === 'INPUT' && e.target.type === 'hidden') return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      /* Ensure variant ID & quantity are up-to-date */
      const currentForm = container.querySelector('form');
      if (currentForm) {
        const variantInput = currentForm.querySelector('.qv-buy-variant-id, input[name="id"]');
        if (variantInput && this.currentVariant) {
          variantInput.value = this.currentVariant.id;
        }
        const qtyInput = currentForm.querySelector('input[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = this.quantity || 1;
        }
      }

      const variant = this.currentVariant;
      if (!variant || !variant.available) return;

      /*
       * Always use the fallback approach: add to cart, then redirect
       * to /checkout. Shiprocket intercepts /checkout if it has
       * checkout page takeover enabled, so this works regardless
       * of whether Shiprocket initialized on the quick view form.
       */
      this._fallbackBuyNow();
    };

    /* Use CAPTURING phase (third arg = true) so our handler fires before
       Shiprocket's bubbling-phase handler can call stopPropagation() */
    container.addEventListener('click', container._qvBuyHandler, true);

    /* Try to get Shiprocket to auto-replace our payment button visually */
    if (fastrrInput) {
      this._triggerShiprocketInit(container, fastrrInput);
    }
  }

  /**
   * Fallback Buy Now: add to cart and redirect to Shopify checkout.
   * Only used when Shiprocket is not available.
   */
  async _fallbackBuyNow() {
    const variant = this.currentVariant;
    if (!variant || !variant.available) return;

    /* Find the buy button — might be our original <button> or a Shiprocket-rendered <div> */
    const buyBtn = this.querySelector('.qv-buy-now-btn')
      || this.querySelector('.quick-view__sr-buy-container button')
      || this.querySelector('.quick-view__sr-buy-container .shiprocket-headless')
      || this.querySelector('.quick-view__sr-buy-container [class*="sr-checkout"]');
    if (buyBtn) {
      buyBtn.style.pointerEvents = 'none';
      buyBtn.style.opacity = '0.6';
      buyBtn.textContent = 'Processing\u2026';
    }

    try {
      const config = fetchConfig('json');
      config.body = JSON.stringify({
        items: [{ id: variant.id, quantity: this.quantity }],
      });

      const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Could not add to cart');
      }

      this.close();
      window.location.href = '/checkout';
    } catch (err) {
      console.error('[QuickView] Fallback buy now error:', err);
      this.showToast(err.message || 'Error \u2014 please try again');
      if (buyBtn) {
        buyBtn.style.pointerEvents = '';
        buyBtn.style.opacity = '';
        buyBtn.textContent = 'BUY IT NOW';
      }
    }
  }

  /**
   * Try to trigger Shiprocket to auto-initialize on the quick view's form.
   * Shiprocket looks for getElementById('fastrrCollectionPage') and replaces
   * the shopify-payment-button div with its own checkout button.
   *
   * @param {HTMLElement} container — Container with the buy now form
   * @param {HTMLElement} fastrrInput — The hidden fastrrCollectionPage input
   */
  _triggerShiprocketInit(container, fastrrInput) {
    if (!fastrrInput) return;

    /* Give our input the expected ID so Shiprocket finds it.
       The close() method restores the main page's ID when the modal closes. */
    const mainFastrr = document.getElementById('fastrrCollectionPage');
    if (mainFastrr && mainFastrr !== fastrrInput) {
      mainFastrr.id = '_qv_fastrr_backup';
    }
    fastrrInput.id = 'fastrrCollectionPage';

    /* Try Shiprocket re-init methods with staggered delays */
    if (window.shiprocketCheckoutEvents) {
      try { window.shiprocketCheckoutEvents.newPageLoad?.(); } catch(e) {}
      setTimeout(() => {
        try { window.shiprocketCheckoutEvents.reRenderBuyNowButton?.(); } catch(e) {}
      }, 300);
      /* Second attempt after a longer delay for slow Shiprocket initialization */
      setTimeout(() => {
        try { window.shiprocketCheckoutEvents.reRenderBuyNowButton?.(); } catch(e) {}
      }, 800);
    }

    /* Dispatch section:load event (Shopify theme editor event) */
    try {
      document.dispatchEvent(new CustomEvent('shopify:section:load'));
    } catch(e) {}
  }

  /**
   * POST the current variant + quantity to the cart, update the UI,
   * and publish the cart update event.
   */
  async _handleAddToCart() {
    const variant = this.currentVariant;
    if (!variant || !variant.available) return;

    const atcBtn = this.querySelector('.quick-view__add-to-cart');
    if (!atcBtn) return;

    /* Indicate loading state */
    const originalText = atcBtn.textContent;
    atcBtn.disabled = true;
    atcBtn.textContent = 'Adding…';

    try {
      const config = fetchConfig('json');
      config.body = JSON.stringify({
        items: [{ id: variant.id, quantity: this.quantity }],
      });

      const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';
      const response = await fetch(cartAddUrl, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Could not add to cart');
      }

      /* Fetch updated cart to get accurate totals */
      const cartResponse = await fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const cart = await cartResponse.json();

      /* Update all cart count bubbles in the header */
      document.querySelectorAll('.cart-count-bubble').forEach((bubble) => {
        const span = bubble.querySelector('span[aria-hidden]') || bubble.querySelector('span:not(.visually-hidden)');
        if (span) span.textContent = cart.item_count;
        const srSpan = bubble.querySelector('.visually-hidden');
        if (srSpan) srSpan.textContent = `${cart.item_count} items`;
        bubble.classList.toggle('hidden', cart.item_count === 0);
      });

      /* Publish cart update event so cart drawers / other components react */
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-view', cart });

      this.showToast('Added to cart!');
    } catch (err) {
      console.error('[QuickView] Add to cart error:', err);
      this.showToast(err.message || 'Error adding to cart');
    } finally {
      atcBtn.disabled = !variant.available;
      atcBtn.textContent = variant.available ? originalText : 'Sold Out';
    }
  }



  /* ------------------------------------------------------------------ */
  /*  Helpers — Price Display                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Update the price, compare-at-price, and discount badge for a variant.
   *
   * @param {Object} variant — Variant object
   */
  _updatePriceDisplay(variant) {
    const priceEl = this.querySelector('.quick-view__price');
    const compareEl = this.querySelector('.quick-view__compare-price');
    const discountEl = this.querySelector('.quick-view__discount');

    if (priceEl) {
      priceEl.textContent = this.formatMoney(variant.price);
    }

    const hasCompare = variant.compare_at_price && variant.compare_at_price > variant.price;

    if (compareEl) {
      if (hasCompare) {
        compareEl.textContent = this.formatMoney(variant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.textContent = '';
        compareEl.style.display = 'none';
      }
    }

    if (discountEl) {
      if (hasCompare) {
        const pct = Math.round((1 - variant.price / variant.compare_at_price) * 100);
        discountEl.textContent = `${pct}% OFF`;
        discountEl.style.display = '';
      } else {
        discountEl.textContent = '';
        discountEl.style.display = 'none';
      }
    }
  }

  /**
   * Update the availability badge.
   *
   * @param {Object} variant — Variant object
   */
  _updateAvailability(variant) {
    const availEl = this.querySelector('.quick-view__availability');
    if (!availEl) return;

    if (variant.available) {
      availEl.textContent = 'In Stock';
      availEl.classList.remove('is-sold-out');
      availEl.classList.add('is-in-stock');
    } else {
      availEl.textContent = 'Sold Out';
      availEl.classList.remove('is-in-stock');
      availEl.classList.add('is-sold-out');
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Helpers — Formatting                                               */
  /* ------------------------------------------------------------------ */

  /**
   * Format a price in cents to the Indian Rupee display format.
   *
   * @param {number} cents — Price in cents (paise)
   * @returns {string} e.g. "Rs. 1,499.00"
   */
  formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return `Rs. ${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  }

  /**
   * Parse product description HTML into collapsible sections.
   * Splits on bold headings (<strong>, <b>, <h1>-<h6>) to create
   * accordion sections matching the product page's collapsible menus.
   *
   * @param {string} html — Product description HTML
   * @returns {Array<{title: string, content: string}>}
   */
  _parseDescriptionSections(html) {
    if (!html) return [];

    const container = document.createElement('div');
    container.innerHTML = html;

    const sections = [];
    let currentTitle = null;
    let currentContent = [];

    const flushSection = () => {
      if (currentTitle && currentContent.length > 0) {
        const contentHtml = currentContent.join('').trim();
        if (contentHtml) {
          sections.push({ title: currentTitle, content: contentHtml });
        }
      }
      currentContent = [];
    };

    /* Walk through top-level nodes */
    const nodes = container.childNodes;
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];

      if (node.nodeType === Node.TEXT_NODE) {
        if (currentTitle) {
          const text = node.textContent.trim();
          if (text) currentContent.push(text);
        }
        continue;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) continue;

      const el = node;
      const tagName = el.tagName.toLowerCase();

      /* Check if this element IS a heading */
      const isHeading = /^h[1-6]$/.test(tagName);

      /* Check if this element contains ONLY a <strong>/<b> — treat as section header */
      const innerStrong = el.querySelector('strong, b');
      const isBoldParagraph = innerStrong &&
        this._stripHtml(el.innerHTML).trim() === this._stripHtml(innerStrong.innerHTML).trim() &&
        this._stripHtml(innerStrong.innerHTML).trim().length > 2;

      if (isHeading || isBoldParagraph) {
        flushSection();
        const titleText = (isHeading ? el.textContent : innerStrong.textContent)
          .replace(/:$/, '').trim();
        if (titleText) {
          currentTitle = titleText;
        }
      } else if (currentTitle) {
        /* Append element HTML to current section */
        currentContent.push(el.outerHTML || `<p>${el.textContent}</p>`);
      }
    }

    /* Flush last section */
    flushSection();

    /* Only return sections if we found at least 2 (otherwise it's not structured) */
    return sections.length >= 2 ? sections : [];
  }

  /**
   * Strip HTML tags from a string to produce plain text.
   *
   * @param {string} html — HTML string
   * @returns {string}
   */
  _stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }

  /**
   * Build a Shopify CDN sized-image URL by inserting the size parameter.
   * Falls back to the original URL if parsing fails.
   *
   * @param {string} src  — Original image URL
   * @param {string} size — e.g. '800x', '100x100'
   * @returns {string}
   */
  _sizedImageUrl(src, size) {
    if (!src) return '';
    try {
      /* Shopify CDN images support _800x or _100x100 suffix before extension */
      const match = src.match(/^(.+)(\.[a-z]+)(\?.*)?$/i);
      if (match) {
        return `${match[1]}_${size}${match[2]}${match[3] || ''}`;
      }
    } catch (e) { /* fall through */ }
    return src;
  }

  /**
   * Find the index of a variant's featured image inside product.images.
   * Compares by stripping protocol and size suffixes for a reliable match.
   *
   * @param {string[]} images — Product images array
   * @param {string}   src    — Variant featured_image.src
   * @returns {number} — Index or -1
   */
  _findImageIndex(images, src) {
    if (!src || !images) return -1;

    /* Normalise by stripping protocol & query string */
    const normalise = (url) =>
      url.replace(/^https?:/, '').replace(/\?.*$/, '').replace(/_\d+x\d*/, '');

    const normSrc = normalise(src);
    return images.findIndex((img) => normalise(img) === normSrc);
  }

  /**
   * Safely extract the option name string from a product option entry.
   * The Shopify /products/{handle}.js API may return options as either:
   *   - A plain string: "Color"
   *   - An object: { name: "Color", position: 1, values: ["Red", "Blue"] }
   *
   * @param {string|Object} opt — Option entry from product.options
   * @returns {string}
   */
  _getOptionName(opt) {
    if (typeof opt === 'string') return opt;
    if (opt && typeof opt === 'object' && opt.name) return opt.name;
    return String(opt);
  }

  /**
   * Check if an option name represents a color option.
   *
   * @param {string} name — Option name
   * @returns {boolean}
   */
  _isColorOption(name) {
    const n = name.toLowerCase().trim();
    return ['color', 'colors', 'colour', 'colours', 'farbe', 'couleur'].includes(n);
  }

  /**
   * Convert a color name to a CSS-compatible hex/color value.
   * Falls back to a neutral grey if the name is not recognised.
   *
   * @param {string} name — Color name (e.g. "Sage Green", "Blush Pink")
   * @returns {string} — CSS color value
   */
  _colorNameToHex(name) {
    const colorMap = {
      /* Whites & Creams */
      'white': '#ffffff',
      'off white': '#faf5ef',
      'off-white': '#faf5ef',
      'ivory': '#fffff0',
      'cream': '#fffdd0',
      'pearl': '#f0ead6',
      'snow': '#fffafa',
      'vanilla': '#f3e5ab',
      'eggshell': '#f0ead6',

      /* Blacks & Greys */
      'black': '#1a1a1a',
      'charcoal': '#36454f',
      'grey': '#9e9e9e',
      'gray': '#9e9e9e',
      'dark grey': '#555555',
      'dark gray': '#555555',
      'light grey': '#d3d3d3',
      'light gray': '#d3d3d3',
      'silver': '#c0c0c0',
      'slate': '#708090',
      'ash': '#b2beb5',
      'graphite': '#474747',
      'smoke': '#b0b0b0',

      /* Reds */
      'red': '#e53e3e',
      'dark red': '#8b0000',
      'maroon': '#800000',
      'burgundy': '#800020',
      'wine': '#722f37',
      'crimson': '#dc143c',
      'scarlet': '#ff2400',
      'cherry': '#de3163',
      'ruby': '#9b111e',
      'vermillion': '#e34234',
      'brick': '#cb4154',
      'rust': '#b7410e',
      'tomato': '#ff6347',

      /* Pinks */
      'pink': '#f8b4c8',
      'blush': '#de5d83',
      'blush pink': '#f9c6cf',
      'rose': '#ff007f',
      'rose pink': '#ff66b2',
      'dusty pink': '#d4a5a5',
      'dusty rose': '#dcae96',
      'hot pink': '#ff69b4',
      'light pink': '#ffb6c1',
      'salmon': '#fa8072',
      'coral': '#ff7f50',
      'coral pink': '#f88379',
      'magenta': '#ff00ff',
      'fuchsia': '#ff00ff',
      'mauve': '#e0b0ff',
      'pastel pink': '#ffd1dc',
      'baby pink': '#f4c2c2',
      'bubblegum': '#ffc1cc',
      'flamingo': '#fc8eac',
      'peach': '#ffcba4',

      /* Oranges */
      'orange': '#ff8c00',
      'burnt orange': '#cc5500',
      'tangerine': '#ff9966',
      'apricot': '#fbceb1',
      'amber': '#ffbf00',
      'copper': '#b87333',
      'terracotta': '#e2725b',
      'terra cotta': '#e2725b',
      'pumpkin': '#ff7518',
      'persimmon': '#ec5800',
      'papaya': '#ffefd5',

      /* Yellows */
      'yellow': '#f6e05e',
      'pale yellow': '#f7f5c4',
      'light yellow': '#fef9c3',
      'mustard': '#e1ad01',
      'gold': '#ffd700',
      'golden': '#ffd700',
      'golden yellow': '#ffdf00',
      'lemon': '#fff44f',
      'sunflower': '#ffda03',
      'honey': '#eb9605',
      'saffron': '#f4c430',
      'butter': '#fffacd',
      'buttercup': '#f9e70d',
      'canary': '#ffef00',
      'maize': '#fbec5d',

      /* Greens */
      'green': '#38a169',
      'dark green': '#006400',
      'light green': '#90ee90',
      'lime': '#32cd32',
      'lime green': '#32cd32',
      'olive': '#808000',
      'olive green': '#6b8e23',
      'sage': '#bcb88a',
      'sage green': '#bcb88a',
      'mint': '#aaf0d1',
      'mint green': '#98fb98',
      'emerald': '#50c878',
      'forest': '#228b22',
      'forest green': '#228b22',
      'teal': '#008080',
      'sea green': '#2e8b57',
      'hunter green': '#355e3b',
      'jade': '#00a86b',
      'pistachio': '#93c572',
      'moss': '#8a9a5b',
      'moss green': '#8a9a5b',
      'army green': '#4b5320',
      'khaki': '#c3b091',
      'dark khaki': '#bdb76b',
      'fern': '#4f7942',
      'basil': '#5c7a29',
      'avocado': '#568203',
      'pine green': '#01796f',
      'chartreuse': '#7fff00',
      'celadon': '#ace1af',
      'spring green': '#00ff7f',
      'turquoise green': '#4de1c4',
      'pista': '#93c572',
      'pista green': '#93c572',
      'bottle green': '#006a4e',
      'parrot green': '#66b032',
      'mehendi': '#8a7d3c',
      'mehendi green': '#8a7d3c',
      'henna': '#7f3f2a',

      /* Blues */
      'blue': '#4299e1',
      'dark blue': '#00008b',
      'light blue': '#add8e6',
      'sky blue': '#87ceeb',
      'baby blue': '#89cff0',
      'powder blue': '#b0e0e6',
      'navy': '#000080',
      'navy blue': '#000080',
      'royal blue': '#4169e1',
      'cobalt': '#0047ab',
      'turquoise': '#40e0d0',
      'aqua': '#00ffff',
      'cyan': '#00ffff',
      'teal blue': '#367588',
      'cerulean': '#007ba7',
      'denim': '#1560bd',
      'sapphire': '#0f52ba',
      'periwinkle': '#ccccff',
      'steel blue': '#4682b4',
      'ice blue': '#d6ecef',
      'cornflower': '#6495ed',
      'indigo': '#4b0082',
      'azure': '#007fff',
      'ocean': '#006994',
      'peacock': '#005f6a',
      'peacock blue': '#005f6a',
      'midnight blue': '#191970',
      'cadet blue': '#5f9ea0',
      'dusty blue': '#6699cc',

      /* Purples & Violets */
      'purple': '#9b59b6',
      'dark purple': '#4a0e4e',
      'light purple': '#d8b4fe',
      'violet': '#8b5cf6',
      'lavender': '#e6e6fa',
      'lilac': '#c8a2c8',
      'plum': '#8e4585',
      'grape': '#6f2da8',
      'amethyst': '#9966cc',
      'orchid': '#da70d6',
      'thistle': '#d8bfd8',
      'wine purple': '#522d5b',
      'aubergine': '#3d0c45',
      'mulberry': '#c64b8c',
      'boysenberry': '#873260',
      'eggplant': '#614051',

      /* Browns */
      'brown': '#795548',
      'dark brown': '#5d4037',
      'light brown': '#a0522d',
      'tan': '#d2b48c',
      'beige': '#f5f5dc',
      'sand': '#c2b280',
      'camel': '#c19a6b',
      'chocolate': '#7b3f00',
      'coffee': '#6f4e37',
      'mocha': '#967969',
      'cocoa': '#875f42',
      'cinnamon': '#d2691e',
      'chestnut': '#954535',
      'walnut': '#5c4033',
      'hazel': '#8e7618',
      'umber': '#635147',
      'sienna': '#a0522d',
      'taupe': '#483c32',
      'mahogany': '#c04000',
      'espresso': '#3c2415',
      'nude': '#f2d2bd',

      /* Naturals & Misc */
      'natural': '#f5e6d3',
      'linen': '#faf0e6',
      'oat': '#dfd0b8',
      'wheat': '#f5deb3',
      'ecru': '#c2b280',
      'bone': '#e3dac9',
      'champagne': '#f7e7ce',
      'flax': '#eedc82',
      'stone': '#928e85',
      'pebble': '#b6ab9e',
      'driftwood': '#a88f65',

      /* Multi/Pattern */
      'multi': 'conic-gradient(#e53e3e, #f6e05e, #38a169, #4299e1, #9b59b6, #e53e3e)',
      'multicolor': 'conic-gradient(#e53e3e, #f6e05e, #38a169, #4299e1, #9b59b6, #e53e3e)',
      'rainbow': 'conic-gradient(#e53e3e, #ff8c00, #f6e05e, #38a169, #4299e1, #4b0082, #9b59b6, #e53e3e)',
    };

    const key = name.toLowerCase().trim();
    if (colorMap[key]) return colorMap[key];

    /* Try to match partial names (e.g., "Sage Green Combo" → "sage green") */
    for (const [colorKey, hex] of Object.entries(colorMap)) {
      if (key.includes(colorKey) && colorKey.length > 2) return hex;
    }

    /* Last resort: try using the name as a CSS named color */
    const testEl = document.createElement('div');
    testEl.style.color = name;
    if (testEl.style.color) return name;

    /* Default fallback — neutral grey */
    return '#b0b0b0';
  }

  /* ------------------------------------------------------------------ */
  /*  Toast                                                              */
  /* ------------------------------------------------------------------ */

  /**
   * Show a brief toast notification at the bottom of the modal.
   *
   * @param {string} message — Message to display
   * @param {number} [duration=2500] — Milliseconds before auto-dismiss
   */
  showToast(message, duration = 2500) {
    /* Remove any existing toast */
    const existing = this.querySelector('.quick-view__toast');
    if (existing) existing.remove();

    const toast = document.createElement('div');
    toast.className = 'quick-view__toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.textContent = message;

    this.appendChild(toast);

    /* Trigger entrance animation on next frame */
    requestAnimationFrame(() => {
      toast.classList.add('is-visible');
    });

    setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      /* Fallback removal if transitionend doesn't fire */
      setTimeout(() => toast.remove(), 400);
    }, duration);
  }
}

/* Register custom element */
if (!customElements.get('quick-view-modal')) {
  customElements.define('quick-view-modal', QuickViewModal);
}

/* ======================================================================
   Event Delegation — Quick View trigger buttons
   ====================================================================== */

/**
 * Global click handler for `.quick-view-btn[data-product-handle]` buttons.
 * Using event delegation means it works for dynamically-rendered cards
 * (recently viewed, wishlist drawer, AJAX-loaded collections, etc.).
 */
document.body.addEventListener('click', (e) => {
  const trigger = e.target.closest('.quick-view-btn[data-product-handle]');
  if (!trigger) return;

  e.preventDefault();
  e.stopPropagation();

  const handle = trigger.dataset.productHandle;
  if (!handle) return;

  const modal = document.querySelector('quick-view-modal');
  if (modal) {
    modal.open(handle);
  } else {
    console.warn('[QuickView] <quick-view-modal> element not found in the DOM.');
  }
});
